import React from 'react';
import { connect } from 'react-redux';
import { t } from '@lingui/macro';
import { i18n } from '@lingui/core';
import { onChangeMapFilters } from '../../ac/onChangeMapFilters';
import { withDisplayedLayersCheck } from '../../HOC/withDisplayedLayersCheck';
import { MapTimeline } from './MapTimeline';

/**
 * Привязка MapTimeline к слою «Агрегированные треки за месяц».
 *
 * Месяц выбирается на таймлайне (categorical), год — степпером в правом верхнем
 * углу. Сейчас доступен только 2026, поэтому год зафиксирован: стрелки
 * заблокированы, значение некликабельно. Когда появятся другие годы — снять
 * блокировку и пересобирать список месяцев по выбранному году.
 *
 * Состояние — mapFilters.adminAggTracksMonthlyMonth в формате 'YYYY-MM'
 * (тот же, что у MonthSelector сайдбара). Работу по карте (перезапрос тайлов с
 * новым query-param month) делает существующая логика Map.jsx по смене mapFilters.
 *
 * Монтируется только когда слой активен (withDisplayedLayersCheck).
 */

const HISTORY_START_YEAR = 2026;
const HISTORY_START_MONTH = 1;
// Пока доступен только один год; год зафиксирован на таймлайне
const FIXED_YEAR = 2026;

// Список доступных месяцев 'YYYY-MM' от начала истории до прошлого завершённого
// месяца включительно (как в MonthSelector сайдбара).
function availableMonths () {
    const now = new Date();
    const endYear = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
    const endMonth = now.getMonth() === 0 ? 12 : now.getMonth();
    const out = [];
    let y = HISTORY_START_YEAR;
    let m = HISTORY_START_MONTH;
    while (y < endYear || (y === endYear && m <= endMonth)) {
        out.push(`${y}-${String(m).padStart(2, '0')}`);
        m += 1;
        if (m > 12) { m = 1; y += 1 }
    }
    return out;
}

const monthLabel = monthNum => new Date(FIXED_YEAR, monthNum - 1, 1).toLocaleString(i18n.locale || 'ru', { month: 'long' });
const AdminAggTracksMonthlyTimelineComponent = ({ month, setMonth }) => {
    // Месяцы выбранного (фиксированного) года как categorical-домен
    const domain = React.useMemo(() => {
        const months = availableMonths()
            .filter(ym => Number(ym.slice(0, 4)) === FIXED_YEAR)
            .map(ym => Number(ym.slice(5, 7)));
        return {
            type: 'categorical',
            values: months.map(m => ({ value: m, label: monthLabel(m) })),
            format: m => monthLabel(m),
        };
    }, []);
    // Текущий месяц-номер из 'YYYY-MM'; фолбэк — последний доступный
    const lastMonth = domain.values.length ? domain.values[domain.values.length - 1].value : 1;
    const cursor = month ? Number(month.slice(5, 7)) : lastMonth;
    const onCursorChange = monthNum => {
        const next = `${FIXED_YEAR}-${String(monthNum).padStart(2, '0')}`;
        if (next !== month) setMonth(next);
    };

    return (
        <MapTimeline
            domain={domain}
            cursor={cursor}
            onCursorChange={onCursorChange}
            stepperControl={{
                display: String(FIXED_YEAR),
                prevDisabled: true, // год зафиксирован: доступен только 2026
                nextDisabled: true,
                // onValueClick не задан — значение некликабельно
            }}
        />
    );
};
const mapStateToProps = ({ mapFilters: { adminAggTracksMonthlyMonth } }) => ({ month: adminAggTracksMonthlyMonth });
const mapDispatchToProps = dispatch => ({
    setMonth: value => dispatch(onChangeMapFilters('adminAggTracksMonthlyMonth', value)),
});
const ConnectedComponent = connect(mapStateToProps, mapDispatchToProps)(AdminAggTracksMonthlyTimelineComponent);

export const AdminAggTracksMonthlyTimeline = withDisplayedLayersCheck(
    ConnectedComponent,
    t`Агрегированные треки за месяц`
);
